import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const CONTACT_EMAIL = import.meta.env?.VITE_CONTACT_EMAIL || 'info@sviluppoweb.app';
const LANDING_SOURCE = 'Modulo Ordine Rivenditore';
const CONTACT_SUBJECT = 'Nuova richiesta - Modulo Ordine Rivenditore';
const FORM_FROM_NAME = 'Sviluppoweb.app';

const initialFormData = {
  name: '',
  email: '',
  phone: '',
  company: '',
  orderChannels: '',
  resellerCount: '',
  message: '',
};

const resellerCountOptions = [
  { value: '', label: 'Seleziona fascia' },
  { value: '1-5', label: '1-5 rivenditori/agenti/clienti ricorrenti' },
  { value: '6-20', label: '6-20 rivenditori/agenti/clienti ricorrenti' },
  { value: '21-50', label: '21-50 rivenditori/agenti/clienti ricorrenti' },
  { value: '51-100', label: '51-100 rivenditori/agenti/clienti ricorrenti' },
  { value: '100+', label: 'Oltre 100 rivenditori/agenti/clienti ricorrenti' },
];

const textareaClasses = "flex min-h-[132px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";
const selectClasses = "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

const buildMessage = (formData) => [
  `Origine richiesta: ${LANDING_SOURCE}`,
  `Nome: ${formData?.name || '-'}`,
  `Email: ${formData?.email || '-'}`,
  `Telefono: ${formData?.phone || '-'}`,
  `Azienda: ${formData?.company || '-'}`,
  `Canali ordine attuali: ${formData?.orderChannels || '-'}`,
  `Rivenditori/agenti/clienti ricorrenti: ${formData?.resellerCount || '-'}`,
  '',
  'Messaggio:',
  formData?.message || '-',
].join('\n');

const ContactForm = () => {
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const updateField = (field, value) => {
    setFormData((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: '' }));
    setSuccessMessage('');
  };

  const validate = () => {
    const nextErrors = {};

    if (!formData?.name?.trim()) nextErrors.name = 'Inserisci nome';
    if (!formData?.email?.trim()) nextErrors.email = 'Inserisci email';
    if (formData?.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      nextErrors.email = 'Email non valida';
    }
    if (!formData?.company?.trim()) nextErrors.company = 'Inserisci azienda';
    if (!formData?.orderChannels?.trim()) nextErrors.orderChannels = 'Descrivi come ricevi oggi gli ordini';
    if (!formData?.resellerCount?.trim()) nextErrors.resellerCount = 'Seleziona fascia';
    if (!formData?.message?.trim()) nextErrors.message = 'Inserisci messaggio';

    setErrors(nextErrors);
    return Object.keys(nextErrors)?.length === 0;
  };

  const trackLead = () => {
    if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
      window.fbq('track', 'Lead');
    }
  };

  const submitWithWeb3Forms = async (formElement, accessKey) => {
    const payload = new FormData(formElement);
    payload.append('access_key', accessKey);
    payload.set('subject', CONTACT_SUBJECT);
    payload.set('from_name', FORM_FROM_NAME);
    payload.set('replyto', formData?.email || '');
    payload.set('nome', formData?.name || '');
    payload.set('email', formData?.email || '');
    payload.set('telefono', formData?.phone || '');
    payload.set('azienda', formData?.company || '');
    payload.set('come_ricevi_ordini', formData?.orderChannels || '');
    payload.set('numero_rivenditori', formData?.resellerCount || '');
    payload.set('origine', LANDING_SOURCE);
    payload.set('source', LANDING_SOURCE);
    payload.set('landing', LANDING_SOURCE);
    payload.set('messaggio', formData?.message || '');
    payload.set('message', buildMessage(formData));

    const response = await fetch('https://api.web3forms.com/submit', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
      },
      body: payload,
    });

    const data = await response.json();
    if (!response.ok || !data?.success) {
      throw new Error(data?.message || 'Errore Web3Forms');
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    const accessKey = import.meta.env.VITE_WEB3FORMS_ACCESS_KEY;

    if (import.meta.env.DEV) {
      console.log('Web3Forms key presente:', Boolean(accessKey));
    }

    if (!accessKey) {
      if (import.meta.env.DEV) {
        console.error('Chiave Web3Forms mancante');
      }
      setErrors({
        submit: `Non siamo riusciti a inviare la richiesta. Riprova tra qualche minuto oppure scrivici a ${CONTACT_EMAIL}.`,
      });
      setSuccessMessage('');
      return;
    }

    setIsSubmitting(true);

    try {
      await submitWithWeb3Forms(event.currentTarget, accessKey);

      trackLead();
      setSuccessMessage('Richiesta inviata correttamente. Ti risponderemo appena possibile.');
      setFormData(initialFormData);
      setErrors({});
    } catch (error) {
      if (import.meta.env?.DEV) {
        console.error('Errore invio form:', error);
      }
      setErrors({
        submit: `Non siamo riusciti a inviare la richiesta. Riprova tra qualche minuto oppure scrivici a ${CONTACT_EMAIL}.`,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      id="contact-form"
      onSubmit={handleSubmit}
      className="rounded-3xl border border-slate-200 bg-white p-5 shadow-[0_24px_70px_-32px_rgba(15,23,42,0.25)] sm:p-7"
      data-form-open-source="modulo-ordine-rivenditore"
    >
      <input type="hidden" name="source" value={LANDING_SOURCE} />
      <input type="hidden" name="subject" value={CONTACT_SUBJECT} />
      <input type="hidden" name="from_name" value={FORM_FROM_NAME} />
      <input type="hidden" name="origine" value={LANDING_SOURCE} />
      <div className="grid gap-4 md:grid-cols-2">
        <Input
          label="Nome"
          required
          value={formData?.name}
          onChange={(event) => updateField('name', event?.target?.value)}
          error={errors?.name}
          placeholder="Mario Rossi"
        />
        <Input
          label="Email"
          type="email"
          required
          value={formData?.email}
          onChange={(event) => updateField('email', event?.target?.value)}
          error={errors?.email}
          placeholder="mario@azienda.it"
        />
        <Input
          label="Telefono opzionale"
          value={formData?.phone}
          onChange={(event) => updateField('phone', event?.target?.value)}
          placeholder="+39 333 1234567"
        />
        <Input
          label="Azienda"
          required
          value={formData?.company}
          onChange={(event) => updateField('company', event?.target?.value)}
          error={errors?.company}
          placeholder="Nome azienda"
        />
      </div>

      <div className="mt-4 space-y-4">
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">
            Come ricevi oggi gli ordini?
            <span className="ml-1 text-destructive">*</span>
          </label>
          <textarea
            className={textareaClasses}
            value={formData?.orderChannels}
            onChange={(event) => updateField('orderChannels', event?.target?.value)}
            placeholder="WhatsApp, email, telefono, PDF, Excel, messaggi vocali..."
          />
          {errors?.orderChannels ? <p className="text-sm text-destructive">{errors.orderChannels}</p> : null}
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">
            Quanti rivenditori/agenti/clienti ricorrenti gestisci?
            <span className="ml-1 text-destructive">*</span>
          </label>
          <select
            className={selectClasses}
            value={formData?.resellerCount}
            onChange={(event) => updateField('resellerCount', event?.target?.value)}
          >
            {resellerCountOptions?.map((option) => (
              <option key={option?.value || 'empty'} value={option?.value}>
                {option?.label}
              </option>
            ))}
          </select>
          {errors?.resellerCount ? <p className="text-sm text-destructive">{errors.resellerCount}</p> : null}
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">
            Messaggio
            <span className="ml-1 text-destructive">*</span>
          </label>
          <textarea
            className={textareaClasses}
            value={formData?.message}
            onChange={(event) => updateField('message', event?.target?.value)}
            placeholder="Descrivi listino, processo, criticità o obiettivo."
          />
          {errors?.message ? <p className="text-sm text-destructive">{errors.message}</p> : null}
        </div>
      </div>

      {successMessage ? (
        <div className="mt-5 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          {successMessage}
        </div>
      ) : null}

      {errors?.submit ? (
        <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          {errors.submit}
        </div>
      ) : null}

      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm leading-6 text-slate-500">
          Ti rispondiamo con una valutazione concreta in base al tuo flusso ordini attuale.
        </p>
        <Button
          type="submit"
          size="xl"
          loading={isSubmitting}
          disabled={isSubmitting}
          data-track-cta="modulo-ordine-rivenditore-form"
        >
          {isSubmitting ? 'Invio richiesta...' : successMessage ? 'Richiesta inviata' : 'Invia richiesta'}
        </Button>
      </div>
    </form>
  );
};

export default ContactForm;
